import React from 'react';

import { Control } from '@vlabs/uikit';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CustomFilterPopover } from '../components/CustomFilterPopover';
import { hasAnyError } from '../helpers';

const isFilled = (val) => {
  if (typeof val === 'number') return true;
  if (typeof val === 'string') return val.trim() !== '';
  return false;
};

export function ScoreRadioFieldsPopover({
  radioName,
  keys = [],
  scoreFields,
  disabled = false,
  id = 'scoreRadioFields',
  ...props
}) {
  const { t } = useTranslation();
  const { register, setValue, formState: { errors } } = useFormContext();

  const selectedKey = useWatch({ name: radioName });

  const watchedNames = keys.flatMap((k) => {
    const cfg = scoreFields?.[k];
    return cfg ? [cfg.gte, cfg.lt] : [];
  });

  const watchedValues = useWatch({ name: watchedNames }) || [];

  if (!radioName || !keys.length || !scoreFields) return null;

  const activeIndex = keys.findIndex((k) => k === selectedKey);
  const activeGte = activeIndex >= 0 ? watchedValues[activeIndex * 2] : undefined;
  const activeLt = activeIndex >= 0 ? watchedValues[activeIndex * 2 + 1] : undefined;

  const hasFilterValue = Boolean(selectedKey) && (isFilled(activeGte) || isFilled(activeLt));

  const hasError = keys.some((k) => {
    const cfg = scoreFields[k];
    return cfg ? (hasAnyError(errors, cfg.gte) || hasAnyError(errors, cfg.lt)) : false;
  });

  const onSelect = (key) => {
    setValue(radioName, key);

    keys.forEach((k) => {
      if (k === key) return;
      const cfg = scoreFields[k];
      if (!cfg) return;
      setValue(cfg.gte, '');
      setValue(cfg.lt, '');
    });
  };

  return (
    <CustomFilterPopover
      hasError={hasError}
      hasFilterValue={hasFilterValue}
      testId={`popover.${id}`}
      triggerValue={`popover.${id}`}
      {...props}
    >
      {keys.map((key) => {
        const cfg = scoreFields[key];
        if (!cfg) return null;

        const isActive = selectedKey === key;

        return (
          <div key={key}>
            <Control.Radio
              checked={isActive}
              disabled={disabled}
              id={`${id}.${key}`}
              label={t(cfg.labelKey)}
              onChange={() => onSelect(key)}
            />

            <Control.Input
              disabled={disabled || !isActive}
              id={`${key}.gte`}
              label={t('from')}
              placeholder={t('from')}
              {...register(cfg.gte, cfg.validationRules)}
              errors={errors}
              isClearable={!!isActive && !disabled}
              onClear={() => setValue(cfg.gte, '')}
            />

            <div style={{ height: 8 }} />

            <Control.Input
              disabled={disabled || !isActive}
              id={`${key}.lt`}
              label={t('to')}
              placeholder={t('to')}
              {...register(cfg.lt, cfg.validationRules)}
              errors={errors}
              isClearable={!!isActive && !disabled}
              onClear={() => setValue(cfg.lt, '')}
            />
          </div>
        );
      })}
    </CustomFilterPopover>
  );
}
